import { useTranslation } from 'react-i18next';
import { EntitlementStatusBadge } from './EntitlementStatusBadge';
import type { TrialCustomerIdentity } from '../types/trialCustomerIdentity';
import { formatDate, formatDateTime, safeDisplay } from '../utils/trialHelpers';

interface TrialProductTimelineSectionProps {
  identity: TrialCustomerIdentity;
  locale: string;
}

function TimelineRow({
  label,
  value,
  unavailableLabel,
}: {
  label: string;
  value: string | null;
  unavailableLabel: string;
}) {
  return (
    <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-3 sm:gap-3 min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="sm:col-span-2 text-sm text-foreground min-w-0">
        {value ?? <span className="text-muted-foreground italic">{unavailableLabel}</span>}
      </dd>
    </div>
  );
}

export function TrialProductTimelineSection({
  identity,
  locale,
}: TrialProductTimelineSectionProps) {
  const { t } = useTranslation();
  const unavailable = t('trials.identity.unavailable');

  const daysLabel =
    identity.daysRemaining !== null
      ? t('trials.detail.daysRemainingValue', {
          count: Math.max(0, identity.daysRemaining),
        })
      : null;

  return (
    <section className="space-y-3 min-w-0" aria-labelledby="trial-product-timeline-heading">
      <h3 id="trial-product-timeline-heading" className="text-sm font-semibold text-foreground">
        {t('trials.detail.productPlan')}
      </h3>
      <dl className="space-y-2.5 rounded-lg border border-border/80 p-3">
        <TimelineRow
          label={t('trials.identity.product')}
          value={safeDisplay(identity.productName)}
          unavailableLabel={unavailable}
        />
        <TimelineRow
          label={t('trials.identity.plan')}
          value={safeDisplay(identity.planName)}
          unavailableLabel={unavailable}
        />
        <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-3 sm:gap-3">
          <dt className="text-xs font-medium text-muted-foreground">
            {t('trials.columns.entitlementStatus')}
          </dt>
          <dd className="sm:col-span-2">
            <EntitlementStatusBadge status={identity.entitlementStatus} />
          </dd>
        </div>
      </dl>

      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t('trials.detail.timeline')}
      </h4>
      <dl className="space-y-2.5 rounded-lg border border-border/80 p-3">
        <TimelineRow
          label={t('trials.identity.trialStart')}
          value={identity.trialStartedAt ? formatDate(identity.trialStartedAt, locale) : null}
          unavailableLabel={unavailable}
        />
        <TimelineRow
          label={t('trials.identity.trialEnd')}
          value={identity.trialEndsAt ? formatDateTime(identity.trialEndsAt, locale) : null}
          unavailableLabel={unavailable}
        />
        <TimelineRow
          label={t('trials.columns.daysRemaining')}
          value={daysLabel}
          unavailableLabel={unavailable}
        />
      </dl>
    </section>
  );
}
